/**
 * API клієнт для doc.material_requisitions — Вимога-накладна (1С `ТребованиеНакладная`).
 * Списання матеріалів у власне виробництво: Дт23-Кт201 + рух НЗВ '+' (режим 1C_WIP).
 * Permission: production.material_transfer.manage (reuse, як на бекенді).
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface MaterialRequisitionItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  /** ч.663: склад рядка (NULL = «Склад джерело» шапки). */
  warehouse_id?: string | null;
  uom_id?: string | null;
  account_code?: string | null;   // рахунок обліку матеріалу (201/207)
}

export interface MaterialRequisitionItem extends MaterialRequisitionItemIn {
  id: string;
  cost_per_unit: string | null;
  total_cost: string | null;
}

export interface MaterialRequisitionListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  source_warehouse_id: string;
  total_cost: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  state?: CancellableState;
}

export interface MaterialRequisition extends MaterialRequisitionListItem {
  department_id: string | null;
  nomenclature_group_id: string | null;
  cost_account_code: string | null;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  items: MaterialRequisitionItem[];
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface MaterialRequisitionCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  source_warehouse_id: string;
  department_id?: string | null;
  nomenclature_group_id?: string | null;
  cost_account_code?: string | null;
  comment?: string | null;
  project_id?: string | null;
  items?: MaterialRequisitionItemIn[];
}

export type MaterialRequisitionUpdate = Partial<Omit<MaterialRequisitionCreate, 'organization_id'>>;

export const materialRequisitions = {
  list: (params?: {
    organization_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc';
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<MaterialRequisitionListItem>>('/material-requisitions', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<MaterialRequisition>(`/material-requisitions/${id}`).then((r) => r.data),
  create: (payload: MaterialRequisitionCreate) =>
    api.post<MaterialRequisition>('/material-requisitions', payload).then((r) => r.data),
  update: (id: string, payload: MaterialRequisitionUpdate) =>
    api.patch<MaterialRequisition>(`/material-requisitions/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/material-requisitions/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<MaterialRequisition>(`/material-requisitions/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<MaterialRequisition>(`/material-requisitions/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/material-requisitions/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
